<template>
  <v-container fluid>
    <v-card elevation="2" :class="{'mb-5': allowCopy}">
      <v-card-title class="display-1">
        <v-row>
          <v-col cols="8">Transactions</v-col>
          <v-col cols="4">
            <v-text-field
              append-icon="search"
              label="Find"
              single-line
              hide-details
              v-model="search"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-title>

      <data-filter
        :dateFrom="date.from"
        :dateTo="date.to"
        :loading="loading"
        :selectables="selectables"
        :filters="filters"
        :isFilterDate="true"
        @updatePeriod="(val)=>{date = val}"
        @updateFilters="(val)=>{filterValues = val}"
        @search="updateTable"
        class="pb-3"
      ></data-filter>

      <v-container fluid class="pb-3">
        <v-row>
          <v-col cols="12" md="4">
            <v-tooltip bottom>
              <span>By enabling sales report mode, transactions are filtered by the date they are recorded into the system, instead of the actual transaction time.</span>
              <template v-slot:activator="{ on }">
                <v-switch
                  :label="`Report mode`"
                  v-model="byCreateDate"
                  v-on="on"
                  @click.native="updateTable"
                  :disabled="loading"
                >
                </v-switch>
              </template>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-container>

      <v-data-table
        :loading="loading"
        :items="formattedTransactions"
        :headers="headers"
        :footer-props="{'items-per-page-options': [15, 30, 50, 100]}"
        :options.sync="pagination"
        ref="transactions-table"
        :search="search"
        hide-default-footer
        mobile-breakpoint="0"
      >
        <template v-slot:item.receipt.receipt_url="{ item }">
          <a
            v-if="item.receipt.receipt_url"
            :href="item.receipt.receipt_url"
            target="_blank"
            style="text-decoration: none;"
          ><span style="text-decoration: underline;">{{ item.receipt.receipt_url }}</span> <v-icon>mdi-open-in-new</v-icon></a>
        </template>
      </v-data-table>

      <v-divider></v-divider>

      <v-card-actions v-if="!loading">
        <paginator
          :pagination.sync="pagination"
          :total-items="totalItems"
          :items-per-page-options="itemsPerPageOptions"
        ></paginator>
      </v-card-actions>
    </v-card>

    <v-btn
      transition="scale-transition"
      v-if="allowCopy"
      fixed
      dark
      fab
      bottom
      right
      color="pink"
      @click.native="exportAsExcel"
    >
      <v-icon>cloud_download</v-icon>
    </v-btn>
  </v-container>
</template>
<script>
  import { mapGetters } from 'vuex'
  import { get, pick, map } from 'lodash'
  import XLSX from 'xlsx'

  import moment from 'moment-timezone'
  import datetimeMixin from '@/mixins/datetime'
  import paginationMixin from '@/mixins/pagination'
  import productMixin from '@/mixins/product'
  import DataFilter from '@/components/Common/DataFilter'
  import Paginator from '@/components/Paginator'

  const fixPriceFilter = (value, zeroValue = '-', decimal = 1) => {
    if (!value) return zeroValue

    return value.toFixed(decimal).replace(/(\d)(?=(\d{3})+\.)/g, '$1,')
  }

  export default {
    props: {
      product: Object,
      location: Object,
      productService: Object,
      transactionService: Object,
      locationService: Object,
      showCost: Boolean,
      externalMemberId: String,
    },

    mixins: [
      datetimeMixin,
      paginationMixin,
      productMixin
    ],

    components: {
      DataFilter,
      Paginator
    },

    data () {
      return {
        loading: true,
        byCreateDate: true,
        selectables: {
          'products': [],
          'locations': [],
          'payment_methods': [
            {
              text: 'Octopus',
              value: 'octopus'
            },
            {
              text: 'Alipay',
              value: 'alipay'
            },
            {
              text: 'Wechat Pay',
              value: 'wechat_pay'
            },
            {
              text: 'UnionPay',
              value: 'union'
            },
            {
              text: 'Cash',
              value: 'cash'
            },
            {
              text: 'Credit Card',
              value: 'credit_card'
            }
          ],
        },
        filterValues: {},

        filters: {
          product_id: {
            items: 'products',
            text: 'Product',
            value: null
          },
          location_id: {
            items: 'locations',
            text: 'Location',
            value: null
          },
          payment_method: {
            items: 'payment_methods',
            text: 'Payment Method',
            value: null
          }
        },

        date: {
          from: moment().startOf('month').unix(),
          to: moment().unix()
        },

        transactions: [],
        products: [],
        locations: [],

        search: null
      }
    },

    computed: {
      ...mapGetters('acl', [
        'matchRoles'
      ]),

      transactionPagination () {
        return Object.assign(this.pagination, {
          sortBy: this.byCreateDate ? ['created_at'] : ['transacted_at'],
        })
      },

      allowCopy () {
        return !this.loading && this.transactions.length !== 0
      },

      headers () {
        const baseHeaders = [
          {
            text: this.byCreateDate ? 'Logged Time' : 'Transaction Time',
            value: this.byCreateDate ? 'created_at' : 'transacted_at',
            align: 'left'
          },
          {
            text: 'Location',
            value: 'location.name',
            align: 'left',
            sortable: false,
          },
          {
            text: 'SKU',
            value: 'product.sku',
            align: 'left',
            sortable: false,
          },
          {
            text: 'Product',
            value: 'product.name_zh_hk',
            align: 'left',
            sortable: false,
          },
          {
            text: 'Size',
            value: 'product.size',
            align: 'left',
            sortable: false,
          },
          {
            text: 'Payment Method',
            value: 'payment_method',
            align: 'left',
            sortable: false,
          },
          {
            text: 'Promotion',
            value: 'promotion.name',
            align: 'left',
            sortable: false,
          },
          {
            text: 'Transaction Amount ($)',
            value: 'amount',
            align: 'left',
            sortable: false,
          },
        ]

        if (this.showCost) {
          baseHeaders.push(
            {
              text: 'Product Cost',
              value: 'cost_product',
              align: 'left',
              sortable: false,
            },
            {
              text: 'Transaction Fee',
              value: 'cost_transaction',
              align: 'left',
              sortable: false,
            },
            {
              text: 'Amount (NET)',
              value: 'amount_net',
              align: 'left',
              sortable: false,
            }
          )
        }

        if (this.matchRoles('chowtaifook')) {
          baseHeaders.push(
            {
              text: 'Is Voided',
              value: 'is_valid',
              align: 'left',
              sortable: false,
            },
            {
              text: 'Include Paper Bag',
              value: 'include_shopping_bag',
              align: 'left',
              sortable: true,
            },
            {
              text: 'Staff Card',
              value: 'staff_card_id',
              align: 'left',
              sortable: true,
            },
            {
              text: 'Product Type',
              value: 'product_extra_data.type',
              align: 'left',
              sortable: false,
            },
            {
              text: 'Material',
              value: 'product_extra_data.material',
              align: 'left',
              sortable: false,
            },
            {
              text: 'Jewel Setting',
              value: 'product_extra_data.jewel',
              align: 'left',
              sortable: false,
            },
            {
              text: 'BBPos Transaction ID',
              value: 'bbpos_transaction_id',
              align: 'left',
              sortable: false,
            },
            {
              text: 'Promotion Code',
              value: 'promotion_code',
              align: 'left',
              sortable: false,
            },
            {
              text: 'Receipt Channel',
              value: 'receipt.channel',
              align: 'left',
              sortable: false,
            },
            {
              text: 'Recipient',
              value: 'receipt.recipient',
              align: 'left',
              sortable: false,
            },
            {
              text: 'Receipt URL',
              value: 'receipt.receipt_url',
              align: 'left',
              sortable: false,
            },
            {
              text: 'Agreement',
              value: 'receipt.agreement',
              align: 'left',
              sortable: false,
            },
            {
              text: 'Reject EDM',
              value: 'receipt.reject_edm',
              align: 'left',
              sortable: false,
            },
            {
              text: 'Reject Partner EDM',
              value: 'receipt.reject_partner_edm',
              align: 'left',
              sortable: false,
            },
            {
              text: 'Gift Dropped',
              value: 'dropped_giveaway',
              align: 'left',
              sortable: false,
            }
          )
        }

        if (this.matchRoles('newlife')) {
          baseHeaders.push(
            {
              text: 'Member Code',
              value: 'member_code',
              align: 'left',
              sortable: true,
            },
          )
        }

        return baseHeaders
      },

      formattedTransactions () {
        return this.transactions.map((transaction) => { return this.formatTransaction(transaction, true) })
      }
    },

    async created () {
      this.loading = true
      this.itemsPerPageOptions = [25, 100, 500]
      this.pagination.itemsPerPage = 25
      this.pagination.sortBy = this.byCreateDate ? ['created_at'] : ['transacted_at']
      this.pagination.sortDesc = [true]

      if (this.product) {
        this.products = [
          {
            text: this.getProductNameWithSku(this.product),
            value: this.product.id
          }
        ]
        this.filters.product_id.value = this.product.id
        this.filters.product_id.hide = true
      } else if (!this.productService) {
        this.filters.product_id.hide = true
      } else {
        this.getProducts()
      }
      if (this.location) {
        this.locations = [
          {
            text: this.location.name,
            value: this.location.id
          }
        ]
        this.filters.location_id.value = this.location.id
        this.filters.location_id.hide = true
      } else if (!this.locationService) {
        this.filters.location_id.hide = true
      } else {
        this.getLocations()
      }

      this.updateTable()
    },

    methods: {
      getDatetimeString (timestamp) {
        return moment.unix(timestamp).tz('Asia/Hong_Kong').format('YYYY-MM-DD HH:mm:ss')
      },

      async updateTable () {
        this.loading = true
        this.transactions = []

        const data = await this.fetchTransactions()

        this.transactions = data.data.data
        this.totalItems = data.data.meta.total
      },

      async fetchTransactions (limit = null) {
        const entries = Object.entries(this.filters)
        for (const [ key, filter ] of entries) {
          if (filter.value !== null) {
            this.filterValues[ key ] = filter.value
          }
        }

        if (this.externalMemberId) {
          this.filterValues['external_member_id'] = this.externalMemberId
        }

        const { data } = await this.transactionService.list(
          this.date.from,
          this.date.to,
          this.page,
          limit || this.pagination.itemsPerPage,
          this.pagination.sortBy[0],
          this.pagination.sortDesc[0] ? 'desc' : 'asc',
          this.filterValues,
          this.byCreateDate ? 'create_date' : 'transaction_date'
        )

        this.loading = false

        if (data.status !== 'success') {
          alert(data.message)
          return
        }

        return data
      },

      async exportAsExcel () {
        let transactions = this.formattedTransactions

        if (this.totalItems > this.pagination.itemsPerPage) {
          // Current list is paginated
          transactions = await this.fetchTransactions(-1).then(({ data }) => {
            return data.data.map((transaction) => this.formatTransaction(transaction, false))
          })
        }

        const headerValues = map(this.headers, 'value')
        transactions = transactions.map(transaction => headerValues.map(key => get(transaction, key)))

        let data = [
          map(this.headers, 'text'),
          ...transactions,
        ]

        let dateHeader = data[0].shift()
        data[0].unshift(dateHeader.replace('Time', 'Date'), dateHeader)

        let length = data.length
        for (let i = 1; i < length; i++) {
          let date = data[i].shift()
          data[i].unshift(...date.split(' '))
        }

        const ws = XLSX.utils.aoa_to_sheet(data)

        const wb = XLSX.utils.book_new()

        XLSX.utils.book_append_sheet(wb, ws)

        XLSX.writeFile(wb, 'transactions.xlsx')
      },

      async getProducts () {
        const { data } = await this.productService.list()
        this.selectables[ 'products' ] = data.data.map(product => {
          return {
            text: this.getProductNameWithSku(product),
            value: product.id
          }
        })
      },

      async getLocations () {
        const { data } = await this.locationService.getAll()
        this.selectables[ 'locations' ] = data.data.map(location => {
          return {
            text: location.name,
            value: location.id
          }
        })
      },

      formatTransaction (transaction, includeNumeric = true) {
        const data = {
          amount: includeNumeric ? fixPriceFilter(transaction.amount, '-') : transaction.amount,

          // show cost
          cost_product: includeNumeric ? fixPriceFilter(transaction.cost_product, '-') : transaction.cost_product,
          cost_transaction: includeNumeric ? fixPriceFilter(transaction.cost_transaction, '-', 2) : transaction.cost_transaction,
          amount_net: includeNumeric ? fixPriceFilter(transaction.amount_net, '-', 2) : transaction.amount_net,

          created_at: this.getDatetimeString(transaction.created_at),
          transacted_at: this.getDatetimeString(transaction.transacted_at),
          location: {
            name: transaction.location ? transaction.location.name : '-',
          },
          product: {
            sku: this.getProductSku(transaction.product),
            name_zh_hk: transaction.product.name_zh_hk,
            size: transaction.product.size ? transaction.product.size : '-',
          },
          payment_method: transaction.payment_method,
          promotion: {
            name: transaction.promotion ? (transaction.promotion.name || 'Yes') : '-',
          },
          // chowtaifook
          is_valid: transaction.is_valid ? '-' : 'Voided',
          bbpos_transaction_id: transaction.bbpos_transaction_id || null,
          receipt: {
            ...pick(transaction.receipt, ['channel', 'recipient', 'receipt_url']),
            agreement: get(transaction, 'receipt.agreement', false) ? 'Yes' : 'No',
            reject_edm: get(transaction, 'receipt.reject_edm', false) ? 'Yes' : 'No',
            reject_partner_edm: get(transaction, 'receipt.reject_partner_edm', false) ? 'Yes' : 'No',
          },
          dropped_giveaway: get(transaction, 'giveaway_item_id', null) ? 'Yes' : 'No',
          staff_card_id: get(transaction, 'staff_card_id', null),
          promotion_code: get(transaction, 'promotion_code', null),
          include_shopping_bag: get(transaction, 'include_shopping_bag', false) ? 'Yes' : 'No',
          redeemed_products: get(transaction, 'redeemed_products', []),
          product_extra_data: {
            ...pick(transaction.product_extra_data, ['type', 'material', 'jewel'])
          },

          // newlife
          member_code: transaction.member_code ? transaction.member_code : '-',
        }

        if (transaction.payment_method === 'redemption' && data.redeemed_products.length > 0) {
          // Override product sku if transaction is redemption
          data.product.sku = data.redeemed_products.map(product => `${product.item_sku}*${product.count}`).join('\n')
        }

        return data
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/Common/Transaction/TransactionList.vue